import { useCallback, useMemo, useRef } from 'react';
import { usePrintDispatch } from '@mummssoftware/common-ui';
import {
  Printing,
  DEFAULT_UNSIGNED_VERBALS_DASHBOARD_PRINT_CONFIG,
  UNSIGNED_VERBALS_PRINT_SOURCE_SINGLE,
  UNSIGNED_VERBALS_PRINT_SOURCE_BULK_BY_PROVIDER,
} from '@mummssoftware/utils';
import messages from './Components/Messages';
import { patientHeaderToPrintPatient } from './Print/patientHeaderToPrintPatient';

/**
 * Render-prop bridge so class Listing can use FaxProvider + usePrint for unsigned verbals.
 * @param {(handlers: { onPrint?: () => void, onFax?: () => void, disablePrint: boolean }) => React.ReactNode} children
 */
export function UnsignedVerbalsPrintFaxBridge({
  keycloak,
  agency,
  currentUser,
  isDoctor,
  intl,
  ordOpen,
  ordBulkOpen,
  bulkPrintReady,
  unsignedVerbalsPayload,
  loadedBulkVerbals,
  printReady,
  printServiceBusy,
  setPrintServiceBusy,
  children,
}) {
  const snapshotRef = useRef({});

  snapshotRef.current = {
    ordOpen,
    ordBulkOpen,
    bulkPrintReady,
    unsignedVerbalsPayload,
    loadedBulkVerbals,
  };

  const printing = useMemo(
    () =>
      new Printing({
        url: window.env.hosts.printingService,
        keycloak,
        agency,
        currentUser,
      }),
    [keycloak, agency, currentUser],
  );

  const getPrintPromise = useCallback(
    (faxInstructions, emailInstructions) => {
      const s = snapshotRef.current;
      let unsignedVerbals;
      let patient;
      let sourceString;
      if (s.ordOpen) {
        unsignedVerbals = s.unsignedVerbalsPayload;
        patient = patientHeaderToPrintPatient(
          unsignedVerbals?.[0]?.patientInfo,
        );
        sourceString = UNSIGNED_VERBALS_PRINT_SOURCE_SINGLE;
      } else if (s.ordBulkOpen && s.bulkPrintReady) {
        unsignedVerbals = s.loadedBulkVerbals;
        patient = null;
        sourceString = UNSIGNED_VERBALS_PRINT_SOURCE_BULK_BY_PROVIDER;
      } else {
        return Promise.reject(new Error('No unsigned verbals context'));
      }
      return printing.printUnsignedVerbals({
        unsignedVerbals,
        patient: patient || undefined,
        dashboardPrintConfig: DEFAULT_UNSIGNED_VERBALS_DASHBOARD_PRINT_CONFIG,
        sourceString,
        faxInstructions,
        emailInstructions,
      });
    },
    [printing],
  );

  const onBefore = useCallback(() => {
    setPrintServiceBusy(true);
  }, [setPrintServiceBusy]);

  const onAfter = useCallback(() => {
    setPrintServiceBusy(false);
  }, [setPrintServiceBusy]);

  const { print, fax, email } = usePrintDispatch(
    getPrintPromise,
    onBefore,
    onAfter,
  );

  const runAction = useCallback(
    (action) => {
      if (!currentUser) {
        window.alert(
          intl.formatMessage(messages.unsignedVerbalsPrintUserRequired),
        );
        return;
      }
      const s = snapshotRef.current;
      let unsignedVerbals;
      if (s.ordOpen) {
        unsignedVerbals = s.unsignedVerbalsPayload;
      } else if (s.ordBulkOpen && s.bulkPrintReady) {
        unsignedVerbals = s.loadedBulkVerbals;
      }
      if (!unsignedVerbals || !unsignedVerbals.length) {
        window.alert(
          intl.formatMessage(messages.unsignedVerbalsPrintNothingToPrint),
        );
        return;
      }
      action();
    },
    [currentUser, intl],
  );

  const printAllowed = ordOpen || (!isDoctor && ordBulkOpen && bulkPrintReady);
  const faxAllowed = ordOpen || (!isDoctor && ordBulkOpen && bulkPrintReady);
  const emailAllowed = ordOpen || (!isDoctor && ordBulkOpen && bulkPrintReady);

  const onPrint = printAllowed ? () => runAction(print) : undefined;
  const onFax = faxAllowed ? () => runAction(fax) : undefined;
  const onEmail = emailAllowed ? () => runAction(email) : undefined;

  const disablePrint =
    printServiceBusy ||
    (ordOpen && !printReady) ||
    (ordBulkOpen && !bulkPrintReady);

  return children({ onPrint, onFax, onEmail, disablePrint });
}
